import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

import {
  Container,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  WarningAlertBox,
  AlertText,
  ButtonRow,
  KeepShiftButton,
  ConfirmCancelButton
} from './styles';
import PaginatedSearchSelect from '../../../../../../components/PaginatedSearchSelect';
import { searchCancellationReasons } from '../../../../../../modules/masterdata/actions';
import { OptionType } from '../../../../../../interfaces/paginatedSearchSelect.interface';

import { fetchCancellationWarning, selectCancellationWarningData } from '../../../../../../modules/shift';

interface CancelShiftProps {
  onKeepShift: () => void;
  onCancelShift: (data: { reason: string; notes: string; is_free_cancellation: boolean }) => void;
  isLateCancellation?: boolean;
  shiftId?: number;
}

interface ICancellationReasonOption extends OptionType {
  reason: string;
}

const CancelShift: React.FC<CancelShiftProps> = ({
  onKeepShift,
  onCancelShift,
  isLateCancellation = true,
  shiftId
}) => {
  const dispatch = useDispatch<any>();
  const [selectedReason, setSelectedReason] = useState<ICancellationReasonOption | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(false);
  const [isFreeCancellation, setIsFreeCancellation] = useState(false);

  const cancellationWarning = useSelector(selectCancellationWarningData);

  useEffect(() => {
    if (shiftId) {
      dispatch(fetchCancellationWarning(shiftId));
    }
  }, [shiftId, dispatch]);


  const fetchReasons = async (search: string, page: number) => {
    try {
      const response: any = await dispatch(searchCancellationReasons({
        search,
        page,
        page_size: 10
      })).unwrap();

      return {
        options: response.data?.map((item: any) => ({
          id: item.reason_id,
          reason: item.reason,
          ...item
        })) || [],
        hasMore: !!response.next
      };
    } catch (err) {
      console.error('Failed to fetch cancellation reasons:', err);
      return { options: [], hasMore: false };
    }
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      setError(true);
      return;
    }
    onCancelShift({ reason: selectedReason.reason, notes, is_free_cancellation: isFreeCancellation });
  };

  return (
    <Container>
      {/* Header */}
      <ModalHeader>
        <ModalTitle>Cancel Shift</ModalTitle>
        <ModalSubtitle>Are you sure you want to cancel this shift?</ModalSubtitle>
      </ModalHeader>

      {/* Warning Box (Conditional) */}
      {cancellationWarning && cancellationWarning.message && (
        <WarningAlertBox>
          <ChatBubbleOutlineRoundedIcon sx={{ color: '#D89614', mt: 0.3 }} />
          <AlertText>
            {cancellationWarning.message}
          </AlertText>
        </WarningAlertBox>
      )}

      {/* Free Cancellation Option */}
      <Box mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isFreeCancellation}
              onChange={(e) => setIsFreeCancellation(e.target.checked)}
              sx={{ color: '#455A64', '&.Mui-checked': { color: '#0C8A4F' } }}
            />
          }
          label={
            <span style={{ fontSize: '0.9rem', color: '#455A64', fontWeight: 500 }}>
              Allow free cancellation (waive cancellation charges)
            </span>
          }
        />
      </Box>

      {/* Form Fields */}
      <Box display="flex" flexDirection="column" gap={3}>

        {/* Reason Dropdown */}
        <Box>
          {/* Custom Label styling to match image */}
          <Box component="label" sx={{ display: 'block', mb: 1, fontWeight: 500, fontSize: '0.9rem', color: '#455A64' }}>
            Reason For Cancellation<span style={{ color: 'red' }}>*</span>
          </Box>
          <FormControl fullWidth error={error}>
            <PaginatedSearchSelect<ICancellationReasonOption>
              label=""
              placeholder="-Select a reason-"
              fetchOptions={fetchReasons}
              value={selectedReason}
              onChange={(newValue) => {
                setSelectedReason(newValue);
                if (newValue) setError(false);
              }}
              getOptionLabel="reason"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '& fieldset': { borderColor: error ? 'red' : '#E0E0E0' }
                }
              }}
            />
          </FormControl>
        </Box>

        {/* Additional Notes */}
        <Box>
          <Box component="label" sx={{ display: 'block', mb: 1, fontWeight: 500, fontSize: '0.9rem', color: '#455A64' }}>
            Additional Notes (Optional)
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add a comment..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
                '& fieldset': { borderColor: '#E0E0E0' },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#9CA3AF',
                opacity: 1,
              },
            }}
          />
        </Box>
      </Box>

      {/* Actions */}
      <ButtonRow>
        <KeepShiftButton variant="outlined" onClick={onKeepShift}>
          Keep Shift
        </KeepShiftButton>
        <ConfirmCancelButton variant="contained" onClick={handleSubmit}>
          Cancel Shift
        </ConfirmCancelButton>
      </ButtonRow>

    </Container>
  );
};

export default CancelShift;
